import { apiClient, type Paginated } from '@/core/api';
import type { Lead, LeadInput } from './types';

export const BASE = '/crm/leads/';

export interface LeadListParams {
  page?: number;
  search?: string;
  status?: string;
  source?: string;
  company?: number;
  /** `true` → only archived rows; `'all'` → both. Unset/`false` → active only. */
  archived?: boolean | 'all';
  /** DRF `OrderingFilter` — e.g. `created` or `-created`. */
  ordering?: string;
  /** Server-side "my leads" filter; backend reads the requesting user. */
  mine?: boolean;
  /** Filter by lead owner (salesperson) — a User id. */
  owner?: number;
  /** Created-window (DateTime) — ISO bounds; `_before` should be end-of-day. */
  created_after?: string;
  created_before?: string;
  /** Filter to rows carrying this collab tag id. */
  tag?: number;
  /** Qualification (ADR-108): the lead's own fit / segment — `none` = unset. */
  icp_fit?: string;
  icp_segment?: string;
  /** …and its company's profile. */
  industry?: string;
  region?: string;
  country?: string;
  technology?: string;
}

export async function listLeads(params: LeadListParams = {}): Promise<Paginated<Lead>> {
  const { data } = await apiClient.get<Paginated<Lead>>(BASE, { params });
  return data;
}

export async function getLead(id: string | number): Promise<Lead> {
  const { data } = await apiClient.get<Lead>(`${BASE}${id}/`);
  return data;
}

export async function createLead(input: LeadInput): Promise<Lead> {
  const { data } = await apiClient.post<Lead>(BASE, input);
  return data;
}

export async function updateLead(id: string | number, input: Partial<LeadInput>): Promise<Lead> {
  const { data } = await apiClient.patch<Lead>(`${BASE}${id}/`, input);
  return data;
}

/**
 * Soft-archive a lead. The backend keeps the row, flips `is_archived=true`,
 * and hides it from default list queries. Use {@link restoreLead} to reverse.
 */
export async function archiveLead(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/`);
}

export async function restoreLead(id: string | number): Promise<Lead> {
  const { data } = await apiClient.post<Lead>(`${BASE}${id}/restore/`);
  return data;
}

/** Permanently delete an archived lead (hard delete; admin only, irreversible). */
export async function purgeLead(id: string | number): Promise<void> {
  await apiClient.delete(`${BASE}${id}/purge/`);
}

/** Convert this Lead to a Deal (server-side, audited C8). Returns the new Deal. */
export async function convertLead(
  id: string | number,
  input: { deal_name?: string } = {},
): Promise<import('../deals/types').Deal> {
  const { data } = await apiClient.post(`${BASE}${id}/convert/`, input);
  return data as import('../deals/types').Deal;
}
